import { Injectable } from '@angular/core';
import { ShareMode } from '@shared/config/navigation.config';
import { Contact, MailBody } from '@shared/models';
import {
  ContactType,
  TeamChannelContact,
} from '@shared/models/collaboration.model';
import {
  GraphApiAttachment,
  GraphApiBodyContentType,
  GraphApiImportance,
  GraphApiMailBody,
  GraphApiMessageRequest,
  GraphResponse,
  GraphResponseStatus,
  MimeType,
} from '@shared/models/graph-api.model';
import { GraphApiHttpService } from '@shared/services/graph-api-http.service';
import { GraphApiService } from '@shared/services/graph-api.service';
import { EMPTY, Observable, forkJoin, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  defaultIfEmpty,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
} from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CollaborationService {
  public shareMode: ShareMode;

  constructor(
    private graphApiService: GraphApiService,
    private graphApiHttpService: GraphApiHttpService
  ) {}

  public sendEmail(body: MailBody): Observable<any> {
    const graphApiMailBody = this.generateGraphApiMailBody(body);
    return this.graphApiHttpService.sendEmail(graphApiMailBody);
  }

  private generateGraphApiMailBody(body: MailBody): GraphApiMailBody {
    const graphApiBody: GraphApiMailBody = {
      message: {
        subject: body.mailSubject,
        body: {
          contentType: GraphApiBodyContentType.HTML,
          content: body.mailText,
        },
        toRecipients: body.mailTo
          .split(',')
          .map(r => ({ emailAddress: { address: r } })),
        importance: body.priority
          ? GraphApiImportance.HIGH
          : GraphApiImportance.NORMAL,
      },
    };

    if (body.mailCC && body.mailCC !== '') {
      graphApiBody.message.ccRecipients = body.mailCC
        .split(',')
        .map(r => ({ emailAddress: { address: r } }));
    }

    const image: GraphApiAttachment = {
      '@odata.type': '#microsoft.graph.fileAttachment',
      name: 'dashboard.png',
      contentType: MimeType.PNG,
      contentBytes: body.base64Image.split(',')[1],
      contentId: 'image',
    };

    graphApiBody.message.attachments = [image];

    return graphApiBody;
  }

  public sendTeamsMessage(
    contacts: Contact[],
    messageRequest: GraphApiMessageRequest
  ) {
    return forkJoin(
      contacts.map(contact => {
        switch (contact.type) {
          case ContactType.ONE_ON_ONE:
            return this.graphApiService
              .createOneToOneChat(contact)
              .pipe(
                switchMap((oneToOneChat: any) =>
                  this.graphApiHttpService.sendChatMessage(
                    oneToOneChat.id,
                    messageRequest
                  )
                )
              );
          case ContactType.GROUP:
          case ContactType.MEETING:
            return this.graphApiHttpService.sendChatMessage(
              contact.id,
              messageRequest
            );
          case ContactType.TEAM_CHANNEL:
            return this.graphApiHttpService.sendTeamChannelMessage(
              contact as TeamChannelContact,
              messageRequest
            );
          default:
            return EMPTY;
        }
      })
    );
  }

  public getSearchStream(
    subject: Observable<string>
  ): Observable<GraphResponse<Contact[][]>> {
    return subject.pipe(
      startWith(''),
      debounceTime(200),
      distinctUntilChanged(),
      switchMap((text: string) =>
        forkJoin(this.getSearchMetrics(text)).pipe(
          defaultIfEmpty([]),
          map((contacts: Contact[][]) => ({
            status: GraphResponseStatus.COMPLETE,
            value: contacts,
          })),
          catchError(error => {
            console.error(error);
            return of({
              status: GraphResponseStatus.ERROR,
              value: [] as Contact[][],
            });
          }),
          startWith({
            status: GraphResponseStatus.LOADING,
            value: [] as Contact[][],
          })
        )
      )
    );
  }

  private getSearchMetrics(searchText: string): Observable<Contact[]>[] {
    let searchMetrics: Observable<Contact[]>[] = [
      this.graphApiService.getUsers(this.shareMode, searchText),
    ];
    if (this.shareMode === ShareMode.Email) {
      searchMetrics = [
        ...searchMetrics,
        this.graphApiHttpService.getGroups(searchText),
      ];
    } else if (this.shareMode === ShareMode.Teams) {
      searchMetrics = [
        ...searchMetrics,
        this.graphApiService.getGroupsAndMeetingsByName(searchText),
        this.graphApiHttpService.getTeamsChannels(searchText),
      ];
    }
    return searchMetrics;
  }
}
